import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "strata:access_token";

// localStorage-backed access token used as a fallback when cookies are
// blocked by the browser (e.g. iOS Safari inside the Emergent preview iframe).
export function setAccessToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}
export function getAccessToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach Authorization: Bearer header on every request when we have a stored
// token. The backend (`get_current_user`) accepts the cookie OR this header.
api.interceptors.request.use((config) => {
  const tok = getAccessToken();
  if (tok) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${tok}`;
    }
  }
  return config;
});

// Mark a single in-flight redirect so concurrent 401s don't navigate twice.
let redirecting401 = false;

// Global 401 handler — silently bounce the user back to /login instead of
// surfacing a "Not authenticated" toast on every page. /auth/me is the
// "am-I-logged-in?" probe and must NOT cause a redirect (it's expected to
// 401 on first load before cookies exist).
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = (error?.config?.url || "");
    const isMe = url.endsWith("/auth/me") || url.includes("/auth/me");
    if (status === 401 && !isMe) {
      // The stored token may also be stale — clear it.
      setAccessToken(null);
      error.suppressed401 = true;
      if (!redirecting401 && typeof window !== "undefined" && window.location.pathname !== "/login") {
        redirecting401 = true;
        // Soft redirect — let the AuthProvider remount with fresh state.
        try { window.location.href = "/login"; } catch {}
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Sugar wrapper used by toast.error callsites — returns ``null`` for 401s that
// the interceptor already handled (so the brief "Not authenticated" flash
// doesn't appear before the redirect to /login).
export function apiErrorMessage(e, fallback) {
  if (!e) return null;
  // Suppress for 401s regardless of whether the interceptor's mutation stuck,
  // by also reading the status directly.
  if (e.suppressed401) return null;
  if (e.response && e.response.status === 401) return null;
  return formatApiError(e?.response?.data?.detail) || (fallback ?? e?.message) || "Something went wrong.";
}

// Timesheet dashboard data source (Strata | Sheet | Both); the server enforces the super-admin lock.
let tsSource = null;
export const setTimesheetSource = (s) => { tsSource = s; };
api.interceptors.request.use((cfg) => {
  const u = cfg.url || "";
  if (tsSource && /^\/(timesheet|payroll)/.test(u) && !/^\/timesheet\/(raw|sms|employees|jobs|form|source)/.test(u)) {
    cfg.params = { ...(cfg.params || {}), ts_source: tsSource };
  }
  return cfg;
});


export default api;
